/**
 * TAMIX SAAS PLATFORM - Tenant List Component
 * ===========================================
 * Generated: 2025-09-22 - Session 6 Sprint 0
 * CU-001: Administrador Gestiona Tenants - List View
 */

import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { tenantApi } from '@/services/tenantApi'
import type { TenantListQuery, TenantStatus } from '@/types/tenant'

const TenantList: React.FC = () => {
  const queryClient = useQueryClient()
  const [query, setQuery] = useState<TenantListQuery>({
    page: 1,
    limit: 10,
    sort_by: 'created_at',
    sort_order: 'desc'
  })

  // Fetch tenants
  const {
    data: tenantsData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['tenants', query],
    () => tenantApi.getTenants(query),
    {
      keepPreviousData: true
    }
  )

  // Delete tenant mutation
  const deleteTenantMutation = useMutation(
    (tenantId: string) => tenantApi.deleteTenant(tenantId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tenants'])
      }
    }
  )

  // Activate/Suspend tenant mutations
  const activateTenantMutation = useMutation(
    (tenantId: string) => tenantApi.activateTenant(tenantId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tenants'])
      }
    }
  )

  const suspendTenantMutation = useMutation(
    (tenantId: string) => tenantApi.suspendTenant(tenantId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tenants'])
      }
    }
  )

  // Search handler
  const handleSearch = (searchTerm: string) => {
    setQuery(prev => ({
      ...prev,
      search: searchTerm,
      page: 1
    }))
  }

  // Status filter handler
  const handleStatusFilter = (status: TenantStatus | '') => {
    setQuery(prev => ({
      ...prev,
      status: status || undefined,
      page: 1
    }))
  }

  // Pagination handler
  const handlePageChange = (newPage: number) => {
    setQuery(prev => ({ ...prev, page: newPage }))
  }

  // Status badge component
  const getStatusBadge = (status: TenantStatus) => {
    const badges = {
      active: 'bg-success',
      inactive: 'bg-secondary', 
      suspended: 'bg-warning text-dark',
      trial: 'bg-info'
    }
    
    return (
      <span className={`badge ${badges[status]}`}>
        {status.toUpperCase()}
      </span>
    )
  }

  // Handle tenant actions
  const handleActivate = (tenantId: string) => {
    if (window.confirm('¿Confirma que desea activar este tenant?')) {
      activateTenantMutation.mutate(tenantId)
    }
  }

  const handleSuspend = (tenantId: string) => {
    if (window.confirm('¿Confirma que desea suspender este tenant?')) {
      suspendTenantMutation.mutate(tenantId)
    }
  }

  const handleDelete = (tenantId: string) => {
    if (window.confirm('¿Confirma que desea eliminar este tenant? Esta acción no se puede deshacer.')) {
      deleteTenantMutation.mutate(tenantId)
    }
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <h4>Error al cargar tenants</h4>
        <p>{error instanceof Error ? error.message : 'Error desconocido'}</p>
        <button className="btn btn-outline-danger" onClick={() => refetch()}>
          Reintentar
        </button>
      </div>
    )
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="h3 mb-1">Gestión de Tenants</h2>
          <p className="text-muted mb-0">
            Administrar organizaciones de la plataforma
          </p>
        </div>
        <Link to="/tenants/new" className="btn btn-primary">
          <i className="fas fa-plus me-2"></i>
          Nuevo Tenant
        </Link>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre o email..."
                value={query.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={query.status || ''}
                onChange={(e) => handleStatusFilter(e.target.value as TenantStatus | '')}
              >
                <option value="">Todos los estados</option>
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
                <option value="suspended">Suspendido</option>
                <option value="trial">Prueba</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={`${query.sort_by}_${query.sort_order}`}
                onChange={(e) => {
                  const [sort_by, sort_order] = e.target.value.split('_')
                  setQuery(prev => ({ ...prev, sort_by: sort_by as any, sort_order: sort_order as 'asc' | 'desc' }))
                }}
              >
                <option value="created_at_desc">Más reciente</option>
                <option value="created_at_asc">Más antiguo</option>
                <option value="name_asc">Nombre A-Z</option>
                <option value="name_desc">Nombre Z-A</option>
                <option value="current_users_desc">Más usuarios</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tenants Table */}
      <div className="card">
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2">Cargando tenants...</div>
            </div>
          ) : tenantsData?.tenants.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-building fa-3x text-muted mb-3"></i>
              <h4>No hay tenants</h4>
              <p className="text-muted">No se encontraron tenants con los filtros actuales.</p>
              <Link to="/tenants/new" className="btn btn-primary">
                Crear primer tenant
              </Link>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Contacto</th>
                    <th>Usuarios</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {tenantsData?.tenants.map((tenant) => (
                    <tr key={tenant.id}>
                      <td>
                        <div className="fw-semibold">{tenant.name}</div>
                        {tenant.domain && (
                          <div className="text-muted small">{tenant.domain}</div>
                        )}
                      </td>
                      <td>
                        <div>{tenant.contact_email}</div>
                        {tenant.contact_phone && (
                          <div className="text-muted small">{tenant.contact_phone}</div>
                        )}
                      </td>
                      <td>
                        <span className="badge bg-light text-dark">
                          {tenant.current_users} / {tenant.max_users}
                        </span>
                      </td>
                      <td>
                        {getStatusBadge(tenant.status)}
                      </td>
                      <td>
                        {new Date(tenant.created_at).toLocaleDateString('es-MX')}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <Link 
                            to={`/tenants/${tenant.id}`}
                            className="btn btn-outline-primary"
                            title="Ver detalles"
                          >
                            <i className="fas fa-eye"></i>
                          </Link>
                          <Link 
                            to={`/tenants/${tenant.id}/edit`}
                            className="btn btn-outline-secondary"
                            title="Editar"
                          >
                            <i className="fas fa-edit"></i>
                          </Link>
                          
                          {tenant.status === 'suspended' || tenant.status === 'inactive' ? (
                            <button
                              className="btn btn-outline-success"
                              title="Activar"
                              onClick={() => handleActivate(tenant.id)}
                              disabled={activateTenantMutation.isLoading}
                            >
                              <i className="fas fa-play"></i>
                            </button>
                          ) : (
                            <button
                              className="btn btn-outline-warning"
                              title="Suspender"
                              onClick={() => handleSuspend(tenant.id)}
                              disabled={suspendTenantMutation.isLoading}
                            >
                              <i className="fas fa-pause"></i>
                            </button>
                          )}
                          
                          <button
                            className="btn btn-outline-danger"
                            title="Eliminar"
                            onClick={() => handleDelete(tenant.id)}
                            disabled={deleteTenantMutation.isLoading}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {tenantsData && tenantsData.total > tenantsData.limit && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${!tenantsData.has_prev ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(query.page! - 1)}
                    disabled={!tenantsData.has_prev}
                  >
                    Anterior
                  </button>
                </li>
                
                {/* Page numbers */}
                {Array.from({ length: Math.ceil(tenantsData.total / tenantsData.limit) })
                  .slice(Math.max(0, query.page! - 3), Math.min(Math.ceil(tenantsData.total / tenantsData.limit), query.page! + 2))
                  .map((_, index) => {
                    const pageNum = Math.max(1, query.page! - 2) + index
                    return (
                      <li key={pageNum} className={`page-item ${pageNum === query.page ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    )
                  })}
                
                <li className={`page-item ${!tenantsData.has_next ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(query.page! + 1)}
                    disabled={!tenantsData.has_next}
                  >
                    Siguiente
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>
    </div>
  )
}

export default TenantList